import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '../api/client';
import SubscribeForm from '../components/SubscribeForm';
import SubscribeBanner from '../components/SubscribeBanner';
import AdSenseSlot from '../components/AdSenseSlot';
import { SkeletonArticle } from '../components/SkeletonLoader';

export default function PostDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [adjacent, setAdjacent] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setError(false);

    api.get(`/api/public/posts/${slug}`)
      .then((res) => {
        setPost(res.data);
        api.get(`/api/public/posts/${slug}/adjacent`)
          .then((r) => setAdjacent(r.data))
          .catch(() => {});
      })
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else setError(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // 페이지 타이틀 동적 설정
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | HOBBYGOM`;
      const descMeta = document.querySelector('meta[name="description"]');
      if (descMeta && post.summary) descMeta.setAttribute('content', post.summary);
    }
    return () => { document.title = 'HOBBYGOM'; };
  }, [post]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const handleShare = async () => {
    const shareData = { title: post.title, url: window.location.href };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* 취소 무시 */ }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('링크가 복사되었습니다.');
      } catch { /* 무시 */ }
    }
  };

  if (loading) return <SkeletonArticle />;

  if (notFound) return (
    <div className="pub-not-found">
      <h2>글을 찾을 수 없습니다.</h2>
      <Link to="/">← 목록으로 돌아가기</Link>
    </div>
  );

  if (error) return (
    <div className="pub-not-found">
      <h2>글을 불러올 수 없습니다.</h2>
      <p>잠시 후 다시 시도해 주세요.</p>
      <div className="pub-not-found-actions">
        <button className="pub-back-btn" onClick={() => window.location.reload()}>다시 시도</button>
        <Link to="/">← 목록으로</Link>
      </div>
    </div>
  );

  if (!post) return null;

  return (
    <article className="pub-article">
      {/* 상단 내비게이션: 뒤로가기 + 공유 */}
      <div className="pub-article-topnav">
        <button onClick={handleBack} className="pub-back-btn">← 이전으로</button>
        <button onClick={handleShare} className="pub-share-btn" title="링크 공유">
          공유 ↗
        </button>
      </div>

      <header className="pub-article-header">
        {post.category && (
          <Link to={`/?categorySlug=${post.category.slug}`} className="pub-article-cat">
            {post.category.name.toUpperCase()}
          </Link>
        )}
        <h1 className="pub-article-title">{post.title}</h1>
        <div className="pub-article-meta">
          <time className="pub-article-date">{formatDate(post.publishedAt)}</time>
          {post.aiGenerated && (
            <span className="pub-ai-tag" title="이 글은 AI가 작성했거나 AI 도움을 받아 작성되었습니다">
              ✦ AI 작성
            </span>
          )}
        </div>
      </header>

      {/* 상단 구독 CTA 배너 */}
      <SubscribeBanner />

      <div
        className="pub-article-body"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(post.contentHtml || '<p>내용이 없습니다.</p>', {
            USE_PROFILES: { html: true },
            ADD_ATTR: ['target', 'rel'],
          }),
        }}
      />

      <AdSenseSlot position="content" style={{ minHeight: '250px', margin: '32px 0' }} />

      <hr className="pub-article-divider" />

      <SubscribeForm />

      {/* 이전/다음 글 */}
      {(adjacent.prev || adjacent.next) && (
        <nav className="pub-article-adjacent">
          {adjacent.prev && (
            <Link to={`/posts/${adjacent.prev.slug}`} className="pub-adjacent-card pub-adjacent-prev">
              <span className="pub-adjacent-label">← 이전 글</span>
              <span className="pub-adjacent-title">{adjacent.prev.title}</span>
            </Link>
          )}
          {adjacent.next && (
            <Link to={`/posts/${adjacent.next.slug}`} className="pub-adjacent-card pub-adjacent-next">
              <span className="pub-adjacent-label">다음 글 →</span>
              <span className="pub-adjacent-title">{adjacent.next.title}</span>
            </Link>
          )}
        </nav>
      )}

      <div className="pub-article-nav">
        <Link to="/">← 목록으로</Link>
      </div>
    </article>
  );
}
